import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { Truck, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";

const SupplierRmaForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const [supplierId, setSupplierId] = useState(
    claim?.supplier_id || claim?.supplier?.id || "",
  );
  const [supplierClaimReference, setSupplierClaimReference] = useState(
    claim?.supplier_claim_reference || claim?.rma_number || "",
  );
  const [supplierCreditAmount, setSupplierCreditAmount] = useState(
    String(claim?.supplier_credit_amount ?? "0"),
  );

  // Fetch suppliers list
  const { data: suppliersData = [], isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ["suppliers-list-select"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.SUPPLIERS, {
        params: { per_page: 100 },
      });
      return res.data?.data || res.data || [];
    },
  });

  const rmaMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        supplier_id: supplierId || null,
        supplier_claim_reference: supplierClaimReference.trim() || null,
        supplier_credit_amount: supplierCreditAmount
          ? parseFloat(supplierCreditAmount)
          : 0,
      };
      const res = await warrantyApi.supplierClaim(claim.id, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Supplier RMA claim recorded successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to record supplier RMA";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!supplierId) {
      toast.error("Please select a supplier");
      return;
    }
    if (!supplierClaimReference.trim()) {
      toast.error("Please enter a supplier RMA claim reference number");
      return;
    }
    rmaMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Truck className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Raise Supplier RMA Claim</DialogTitle>
            <DialogDescription>
              Track warranty credit and RMA recovery from the manufacturer or vendor
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <Label>Supplier *</Label>
          <ReactSelect
            isLoading={isLoadingSuppliers}
            placeholder="Search supplier..."
            value={
              supplierId
                ? {
                  value: supplierId,
                  label:
                    suppliersData.find((s) => s.id === supplierId)?.name ||
                    suppliersData.find((s) => s.id === supplierId)?.supplier_name ||
                    "Selected Supplier",
                }
                : null
            }
            onChange={(opt) => setSupplierId(opt ? opt.value : "")}
            options={suppliersData.map((s) => ({
              value: s.id,
              label: s.supplier_name || s.name || s.company_name,
            }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rma_reference">Supplier RMA / Claim Reference *</Label>
          <Input
            id="rma_reference"
            value={supplierClaimReference}
            onChange={(e) => setSupplierClaimReference(e.target.value)}
            placeholder="e.g. RMA-88213"
            maxLength={255}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="credit_amount">Expected Supplier Credit Amount ($)</Label>
          <Input
            id="credit_amount"
            type="number"
            step="0.01"
            value={supplierCreditAmount}
            onChange={(e) => setSupplierCreditAmount(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rmaMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={rmaMutation.isPending}>
            {rmaMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting RMA...
              </>
            ) : (
              "Save Supplier Claim"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

SupplierRmaForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const SupplierRmaModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {open && (
          <SupplierRmaForm
            key={claim.id || "supplier"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

SupplierRmaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default SupplierRmaModal;
